import { useEffect, useState } from "react";
import { Headset, RefreshCw, BotMessageSquare, Send, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { useQuickReplies, QuickReplyButtons, QuickRepliesManager } from "@/components/whatsapp/QuickReplies";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");
const WHO = { cliente: ["Cliente", "text-sky-700"], assistente: ["Assistente IA", "text-violet-700"], operatore: ["Operatore", "text-emerald-700"] };

function ReplyBox({ h, onSent, quick }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.waHandoffReply(h.id, text.trim());
      toast.success("Risposta inviata su WhatsApp");
      setText("");
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-1.5" data-testid={`handoff-reply-${h.id}`}>
      <QuickReplyButtons items={quick} handoffId={h.id} onPick={(t) => setText(t)} />
      {!h.window_open && (
        <p className="text-[11px] text-amber-700 flex items-center gap-1" data-testid={`handoff-window-warning-${h.id}`}><AlertTriangle className="h-3 w-3" /> Sono passate più di 24 ore dall'ultimo messaggio del cliente: WhatsApp potrebbe rifiutare un testo libero. Il cliente deve scrivere di nuovo per riaprire la finestra.</p>
      )}
      <div className="flex gap-2 items-end">
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi la risposta al cliente…" className="text-sm bg-white" data-testid={`handoff-reply-input-${h.id}`}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }} />
        <Button size="sm" className="h-9 gap-1.5 shrink-0" disabled={busy || !text.trim()} onClick={send} data-testid={`handoff-reply-send-${h.id}`}><Send className="h-3.5 w-3.5" /> Invia</Button>
      </div>
    </div>
  );
}

export function HandoffsPanel() {
  const [data, setData] = useState({ items: [], waiting: 0 });
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(null);
  const quick = useQuickReplies();
  const load = (all = showAll) => api.waHandoffs(all ? "all" : "waiting").then(setData).catch(() => {});
  useEffect(() => { load(); const t = setInterval(() => load(), 30000); return () => clearInterval(t); }, [showAll]); // eslint-disable-line react-hooks/exhaustive-deps
  const resolve = async (id) => {
    setBusy(id);
    try {
      await api.waHandoffResolve(id);
      toast.success("Agente IA riattivato per questa chat");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione non riuscita");
    } finally {
      setBusy(null);
    }
  };
  return (
    <Card className="p-5 bg-card border-border space-y-3" data-testid="whatsapp-handoffs">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-display font-bold text-lg flex items-center gap-2">
          <Headset className="h-4 w-4" /> Chat in attesa di operatore
          {data.waiting > 0 && <span className="rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 text-xs" data-testid="whatsapp-handoffs-count">{data.waiting}</span>}
        </h3>
        <div className="flex gap-1.5">
          <QuickRepliesManager items={quick.items} onChanged={quick.reload} />
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setShowAll((v) => !v)} data-testid="whatsapp-handoffs-toggle">{showAll ? "Solo in attesa" : "Mostra anche chiuse"}</Button>
          <Button size="sm" variant="secondary" className="h-8" onClick={() => load()} data-testid="whatsapp-handoffs-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">Quando un cliente chiede di parlare con una persona, l'agente IA si mette in pausa per quel numero e ti avvisa via email. Rispondi direttamente da qui (il messaggio parte dal numero WhatsApp Spoki), poi riattiva l'IA.</p>
      {data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="whatsapp-handoffs-empty">Nessuna chat in attesa: l'agente IA sta gestendo tutte le conversazioni.</p>
      ) : (
        <div className="space-y-2">
          {data.items.map((h) => (
            <div key={h.id} className={`rounded-lg border p-3 space-y-2 ${h.status === "waiting" ? "border-amber-300 bg-amber-50" : "border-border bg-secondary/40"}`} data-testid={`handoff-${h.id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="text-sm">
                  <span className="font-mono-code font-semibold">{h.phone}</span>{h.name && <span className="text-muted-foreground"> · {h.name}</span>}
                  <div className="text-xs text-muted-foreground">{fmt(h.created_at)} · {h.reason || "Richiesta operatore"}{h.new_messages ? ` · ${h.new_messages} nuovi messaggi` : ""}</div>
                </div>
                {h.status === "waiting" ? (
                  <Button size="sm" variant="outline" className="h-8 gap-1.5" disabled={busy === h.id} onClick={() => resolve(h.id)} data-testid={`handoff-resolve-${h.id}`}><BotMessageSquare className="h-3.5 w-3.5" /> Riattiva IA</Button>
                ) : (
                  <span className="text-xs text-emerald-700">Chiusa {fmt(h.resolved_at)}{h.resolved_by ? ` da ${h.resolved_by}` : ""}</span>
                )}
              </div>
              <div className="space-y-0.5 rounded-md bg-white/70 border border-border p-2">
                {(h.last_messages || []).slice(-6).map((m, i) => {
                  const [label, cls] = WHO[m.who] || [m.who, ""];
                  return <div key={`${m.at || m.created_at || i}-${m.who}`} className="text-xs"><span className={`font-semibold ${cls}`}>{label}:</span> <span className="text-foreground/80">{m.text}</span></div>;
                })}
              </div>
              {h.status === "waiting" && <ReplyBox h={h} onSent={() => load()} quick={quick.items} />}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
